import {
  FilteractionObject,
  FilterQuery,
  Objectclass
} from '@yes.technology/react-toolkit'
import { isEqual, merge, uniq } from 'lodash'
import { useCallback, useEffect, useMemo } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { useFilterFields } from 'shared/hooks'
import { FilterFieldsResponse } from 'shared/hooks/useFilterFields/useFilterFields'
import useNavigateMergingState from 'shared/hooks/useNavigateMergingState/useNavigateMergingState'
import { FieldSettings } from 'shared/types'
import { ConcatFields } from 'shared/utils/object/concatenateFields'
import useStyleMaps, { StyleMap } from 'site/Renderer/hooks/useStyleMap'
import { useSitemodel } from 'sitemodel/shared/hooks'
import { useSiteState } from 'siteState/shared'
import { FilterForm } from './components'
import { FilterAreaLayout } from './types'

export type FilterFieldsDisplayOptions = {
  [elementUuid: string]: {
    hideFilteraction?: boolean
    hideMainCollapsibleContainer?: boolean
    hideFieldsCollapsibleContainer?: boolean
    hideFields?: boolean
  }
}

type FilterFieldsProps = {
  props: {
    elementUuid: string
    uuid_objectclass: string
    uuid_classification?: string
    defaultExecutedFilteraction?: string
    navigationslider_background_color?: string
    navigationslider_color?: string
    filter_area_layout?: FilterAreaLayout
    field_settings?: FieldSettings
    concat_fields?: ConcatFields
    styleMap?: StyleMap
    filter_query_target_uuid?: string
    filteraction_options?: FilteractionObject[]
    depends_on_filteraction_options?: boolean
    merge_filter_query?: FilterQuery
    enable_composition?: boolean
    embedded?: boolean
    select_and_link_target_path?: string
    hide_filter_fields?: boolean
  }
}

const FilterFields = ({ props }: FilterFieldsProps) => {
  const sitemodel = useSitemodel()

  const {
    elementUuid,
    uuid_objectclass: objectclassUuid,
    uuid_classification: classificationUuid,
    defaultExecutedFilteraction,
    navigationslider_background_color: navigationsliderBackgroundColor,
    navigationslider_color: navigationsliderColor,
    filter_area_layout: filterAreaLayout,
    field_settings: fieldSettings,
    concat_fields: concatFields,
    filter_query_target_uuid: filterQueryTargetUuid,
    styleMap,
    filteraction_options: filteractionOptions,
    merge_filter_query: mergeFilterQuery,
    enable_composition: enableComposition,
    embedded,
    select_and_link_target_path: selectAndLinkTargetPath,
    depends_on_filteraction_options: dependsOnFilteractionOptions,
    hide_filter_fields: hideFilterFields
  } = props

  const styleProp = useStyleMaps(styleMap)
  const {
    filteractions: { hide_filteraction: hideFilteraction = false } = {},
    hide_main_collapsible_container: hideMainCollapsibleContainer = false,
    hide_fields_collapsible_container: hideFieldsCollapsibleContainer = false
  } = filterAreaLayout || {}

  const [, setSiteQuery] = useSiteState<Partial<FilterQuery> | undefined>(
    'filter-query',
    filterQueryTargetUuid
  )
  const [, setObjectIndex] = useSiteState<number | undefined>('object-index')

  const [, setLockObjectViewObject] = useSiteState<boolean>(
    'lock-object-view-object'
  )

  const [siteFields, setSiteFields] =
    useSiteState<FilterFieldsResponse>('filter-fields')
  const [_, setFilterExecutionTime] = useSiteState<Date>(
    'filter-execution-time'
  )
  const [, setObjectclassUUID] = useSiteState<string>('objectclassuuid')
  const [, setFilterObjectclass] = useSiteState<Objectclass | undefined>(
    'filter-objectclass'
  )
  const [, setDisplayOptions] = useSiteState<FilterFieldsDisplayOptions>(
    'filter-display-options'
  )
  const [, setRefetchFilterQuery] = useSiteState(
    'refetch-filterquery',
    filterQueryTargetUuid
  )

  const [, setBoundFilterQueryElements] = useSiteState<string[]>(
    'bound-filter-query-elements'
  )

  const navigateMergingState = useNavigateMergingState()

  const location = useLocation()

  const [searchParams] = useSearchParams()

  const fields = useFilterFields({
    objectclassUuid,
    classificationUuid,
    filterAreaLayout,
    fieldSettings,
    hideFilterFields: !!hideFilterFields
  })

  const initialGroupedFieldValues = useMemo(
    () => ({ 1: fields.initialFieldValues }),
    [fields.initialFieldValues]
  )

  const onResetObjectViewParams = useCallback(() => {
    setObjectIndex(0)

    setLockObjectViewObject(false)

    navigateMergingState(
      {
        search: location.search,
        hash: ''
      },
      {}
    )
  }, [
    setObjectIndex,
    setLockObjectViewObject,
    navigateMergingState,
    location.search
  ])

  useEffect(() => {
    setFilterObjectclass(fields.objectclass)
  }, [fields.objectclass, setFilterObjectclass])

  useEffect(() => {
    setDisplayOptions((prevState) => ({
      ...prevState,
      [elementUuid]: {
        hideFilteraction,
        hideMainCollapsibleContainer,
        hideFieldsCollapsibleContainer,
        hideFields: !!hideFilterFields
      }
    }))
  }, [
    elementUuid,
    hideFieldsCollapsibleContainer,
    hideFilteraction,
    hideFilterFields,
    hideMainCollapsibleContainer,
    setDisplayOptions
  ])

  useEffect(() => {
    if (filterQueryTargetUuid) {
      setBoundFilterQueryElements((prevState) =>
        uniq([...(prevState ?? []), filterQueryTargetUuid])
      )
    }
  }, [filterQueryTargetUuid, setBoundFilterQueryElements])

  const onClickFilter = useCallback(
    (query: Partial<FilterQuery>) => {
      if (isEqual(fields, siteFields)) {
        setRefetchFilterQuery(true)
      }

      const mergedQuery = { ...merge(query, mergeFilterQuery) }

      setSiteQuery(mergedQuery)

      onResetObjectViewParams()

      setSiteFields(fields)
      setObjectclassUUID(objectclassUuid)
      setFilterExecutionTime(new Date())
    },
    [
      fields,
      siteFields,
      mergeFilterQuery,
      setSiteQuery,
      onResetObjectViewParams,
      setSiteFields,
      setObjectclassUUID,
      objectclassUuid,
      setFilterExecutionTime,
      setRefetchFilterQuery
    ]
  )

  const onClearFilterQuery = useCallback(
    (query?: Partial<FilterQuery>) => {
      setSiteQuery(query)
      setObjectIndex(undefined)
    },
    [setSiteQuery, setObjectIndex]
  )

  const key = useMemo(() => JSON.stringify(fields), [fields])

  const filteractionFromQueryParams: string =
    searchParams.get('uuid_filteraction') || ''

  const defaultExecutedFilteractionValue =
    defaultExecutedFilteraction || filteractionFromQueryParams

  if (!fields.classifiedFields[0].length) return null

  const sitemodelTitle = sitemodel?.title
  const objectclassName = fields?.objectclass?.des

  return (
    <FilterForm
      key={key}
      filterAreaLayout={filterAreaLayout}
      initialGroupedFieldValues={initialGroupedFieldValues}
      sections={fields.classifiedFields}
      onSubmit={onClickFilter}
      title={sitemodelTitle || objectclassName}
      uuidCurrentSitemodelObjectclass={props.uuid_objectclass}
      defaultExecutedFilteraction={defaultExecutedFilteractionValue}
      navigationSliderBackgroundColor={navigationsliderBackgroundColor}
      navigationSliderColor={navigationsliderColor}
      style={styleProp}
      {...{
        elementUuid,
        fieldSettings,
        concatFields,
        filteractionOptions,
        selectAndLinkTargetPath
      }}
      clearFilterQuery={onClearFilterQuery}
      enableComposition={enableComposition}
      dependsOnFilteractionOptions={dependsOnFilteractionOptions}
      embedded={embedded}
      hideFields={!!hideFilterFields}
    />
  )
}

export default FilterFields
